import { type FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AdsContext } from '@components/Ads/context';

import { useClaimDailyTgemBonusMutation } from '@/api/queries/user';
import { useCountDownTimer } from '@/common/hooks/count-down-timer';
import { secondsUntilEndOfDayGMT } from '@/common/lib/secondsUntilEndOfDay';
import { timeStringFormat } from '@/common/lib/timer';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { MainSmButton, WatchSmButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import {
  claimDailyTgemBonus,
  clearDailyTgemBonus,
  updateCoinBalance,
} from '@/redux/reducers/userReducer';
import { selectDailyTgemBonus } from '@/redux/selectors/userSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import dailyLeftBg from '/img/shop/shop-daily-tgem-bg-left.svg';
import dailyLeftLight from '/img/shop/shop-daily-tgem-bg-light-left.png';
import dailyRightLight from '/img/shop/shop-daily-tgem-bg-light-right.png';
import dailyRightBg from '/img/shop/shop-daily-tgem-bg-right.svg';
import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

const DailyBanner: FC = () => {
  const { t } = useTranslation();
  const dailyTgemsBonus = useAppSelector(selectDailyTgemBonus);
  const hasVipStatus = useAppSelector((state) => state.user.user?.VIP);
  const secondToEndDay = useCountDownTimer(secondsUntilEndOfDayGMT());
  const [claimDailyBonus, { isLoading }] = useClaimDailyTgemBonusMutation();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (secondToEndDay <= 0) {
      dispatch(clearDailyTgemBonus());
    }
  }, [secondToEndDay]);

  const claimBonus = (type: 'free' | 'advertising'): void => {
    claimDailyBonus({ type })
      .unwrap()
      .then(({ tgem }) => {
        dispatch(claimDailyTgemBonus(type));
        dispatch(updateCoinBalance(tgem));
      });
  };

  const claimAdsBonus = (): void => {
    if (hasVipStatus) {
      claimBonus('advertising');
    } else {
      showAd({
        placements: 'daily',
        onReward: () => {
          claimBonus('advertising');
        },
        onError: () => {
          showToast(t('Ads launch error'));
        },
      });
    }
  };

  return (
    <div className="w-[calc(100%_-_2rem)] relative mb-6 mx-4">
      <div className={css.container}>
        <img
          src={dailyLeftBg}
          alt="left bacckground"
          className="h-full left-0 top-0 bottom-0 absolute"
        />
        <img
          src={dailyRightBg}
          alt="right bacckground"
          className="h-full right-0 top-0 bottom-0 absolute"
        />
        <img
          src={dailyLeftLight}
          alt="left light"
          className="h-full left-0 top-0 bottom-0 absolute mix-blend-plus-lighter"
        />
        <img
          src={dailyRightLight}
          alt="right light"
          className="h-full right-0 top-0 bottom-0 absolute mix-blend-plus-lighter"
        />

        <div className="flex items-center gap-2">
          <Picture
            img={tgemPng}
            webp={tgemWebp}
            alt="t gem"
            className="size-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]"
          />
          <span className="font-extrabold text-2xl leading-none [text-shadow:0_1px_1px_rgba(0,0,0,0.2)] ">
            +1
          </span>
        </div>
        <div className="relative flex-1 max-w-[8.5rem]">
          {dailyTgemsBonus && (
            <>
              {dailyTgemsBonus.free ? (
                <>
                  <MainSmButton
                    text={t('Free')}
                    disabled={isLoading}
                    onClick={() => claimBonus('free')}
                    isLoading={isLoading}
                  />
                  <div className="absolute -top-1 -right-1">
                    <Ticker
                      text={t('daily')}
                      className="uppercase"
                      variant="gold-small"
                    />
                  </div>
                </>
              ) : dailyTgemsBonus.advertising ? (
                <WatchSmButton
                  text={hasVipStatus ? 'Free' : 'Watch'}
                  isVIP={hasVipStatus}
                  onClick={claimAdsBonus}
                  disabled={isLoading || adsIsLoading}
                  isLoading={adsIsLoading}
                />
              ) : (
                <MainSmButton
                  text={timeStringFormat(secondToEndDay * 1000)}
                  inactive={true}
                  disabled={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export { DailyBanner };

// <div className="absolute right-0 -top-[8px]">
//   <Ticker variant="gold" text={t('daily')} />
// </div>
